import Box from '@/common/components/Box'
import Select from '@/common/components/Select'
import { UnitTextField } from '@/common/components/UnitTextField'
import { unities, unitiesDecimals } from '@/common/constants/unities.constant'
import useDebounce from '@/common/hooks/useDebounce'
import { Option } from '@/common/types/option'
import { cn } from '@/common/utils/cn'
import { formatDecimalUnit } from '@/common/utils/formatters'
import { unFormatUnknownUnit } from '@/common/utils/unFormatters'
import { useClientCompanyBranchAvailableToAllocateSummary } from '@/features/privateCompanies/hooks/useClientCompanyBranchAvailableToAllocateSummary'
import { GovernmentAllocationEntryProductCategoryQuantityValidationSchema } from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/schemas/update.schema'
import { EntryProductFormItemQuantity } from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/Update/components/EntryProductFormItemQuantity'
import { ClientCompanyAllowedCategoriesAndProductsTransformer } from '@/features/privateCompanies/services/allowedCategoriesAndProductsTransformer'
import {
  ClientCompanyAllowedCategoriesAndProducts,
  ClientCompanyAllowedCategoryAndProductData,
} from '@/features/privateCompanies/types/allowedCategoriesAndProducts.type'
import {
  CompanyGovernmentAllocationEntryProduct,
  CompanyGovernmentAllocationEntryProductCategory,
} from '@/features/privateCompanies/types/companyGovernmentAllocationEntry.type'
import {filterOptions} from '@/features/users/constants/filterOptions.constant'
import {BoxesIcon, SearchIcon} from 'lucide-react'
import {useMemo} from 'react'
import {useFieldArray, useFormContext, useWatch} from 'react-hook-form'
import {FilterOptionOption} from 'react-select'

interface EntryProductCategoryQuantityProps {
  companyBranchId?: string
  entryProductCategory: CompanyGovernmentAllocationEntryProductCategory
  allowedCategorySummary: ClientCompanyAllowedCategoryAndProductData
  disabled: boolean
}

export const EntryProductCategoryQuantity = ({
  companyBranchId,
  entryProductCategory,
  allowedCategorySummary,
  disabled,
}: EntryProductCategoryQuantityProps) => {
  const {
    control,
    register,
    watch,
    setValue,
    formState: {errors},
  } = useFormContext<GovernmentAllocationEntryProductCategoryQuantityValidationSchema>()

  const [searchProduct, setSearchProduct] = useDebounce('')

  const {data: allowedCategoriesAndProducts, isPending: isAllowedCategoriesAndProductsPending} =
    useClientCompanyBranchAvailableToAllocateSummary<ClientCompanyAllowedCategoriesAndProducts>({
      companyBranchId,
      filters: {
        search: searchProduct,
        productCategoryId: entryProductCategory?.product_category?.id,
      },
    })

  const allowedProductsOptions = useMemo(() => {
    return isAllowedCategoriesAndProductsPending ||
      !allowedCategoriesAndProducts ||
      !allowedCategoriesAndProducts['products']
      ? []
      : ClientCompanyAllowedCategoriesAndProductsTransformer.optionsTransformer({
        key: 'products',
        controlledBy: 'quantity',
      })(allowedCategoriesAndProducts || {})
  }, [isAllowedCategoriesAndProductsPending])

  const {
    fields: entryProducts,
    append: appendEntryProduct,
    remove: removeEntryProduct,
  } = useFieldArray({
    control,
    name: 'entry_products',
  })

  const watchCurrentCategoryGrantedQuantity = useWatch({
    control,
    name: 'total_quantity',
  })

  const availableQuantity = Number(allowedCategorySummary?.available_quantity) || 0

  const availableForAllocation = Math.max(
    availableQuantity - Number(unFormatUnknownUnit(watchCurrentCategoryGrantedQuantity) || 0),
    0,
  )

  const setProductCategoryTotalQuantity = () => {
    setTimeout(() => {
      const productCategoriesTotalQuantitySum = entryProducts.reduce((sum, _, index) => {
        const productTotalValue = watch(`entry_products.${index}.total_quantity`);

        return sum + (productTotalValue ? unFormatUnknownUnit(productTotalValue) : 0);
      }, 0);

      setValue(`total_quantity`, formatDecimalUnit(productCategoriesTotalQuantitySum, productCategoryUnit));
    });
  };

  const productCategoryUnit = useMemo(() => {
    const firstProductUnit = (entryProducts || [])[0]?.product.unit_for_quantity;

    return (entryProducts || []).every(p => p.product.unit_for_quantity === firstProductUnit)
      ? firstProductUnit
      : undefined;
  }, [(entryProducts || []).length]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between">
        <div className="flex items-center gap-x-2">
          <BoxesIcon strokeWidth={1} />

          <p>{entryProductCategory?.product_category?.display_name}</p>
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <UnitTextField
          label="Quantidade total do serviço"
          unit={productCategoryUnit ? unities[productCategoryUnit] : undefined}
          decimals={productCategoryUnit ? unitiesDecimals[productCategoryUnit] : 2}
          max={999_999_999.99}
          autoCorrect="off"
          {...register('total_quantity')}
          value={watchCurrentCategoryGrantedQuantity}
          forceValue
          markAsRequired
          errorMessage={errors.total_quantity?.message || undefined}
          disabled={disabled || entryProducts.length > 0}
        />

        {allowedCategorySummary && (
          <div className="flex gap-3">
            <p className="italic text-xs text-white-400">
              Quantidade possível:{' '}
              {allowedCategorySummary?.available_quantity
                ? formatDecimalUnit(Number(allowedCategorySummary?.available_quantity), productCategoryUnit)
                : '--'}
            </p>

            <p className="italic text-xs text-white-400">
              Disponível para distribuir: {formatDecimalUnit(Number(availableForAllocation), productCategoryUnit)}
            </p>

            <p className="italic text-xs text-white-400">
              Quantidade garantida:{' '}
              {allowedCategorySummary?.granted_quantity
                ? formatDecimalUnit(Number(allowedCategorySummary?.granted_quantity), productCategoryUnit)
                : '--'}
            </p>

            <p className="italic text-xs text-white-400">
              Quantidade inserida:{' '}
              {allowedCategorySummary?.entry_quantity
                ? formatDecimalUnit(Number(allowedCategorySummary?.entry_quantity), productCategoryUnit)
                : '--'}
            </p>
          </div>
        )}
      </div>

      <Box className="space-y-4 p-4">
        <Select
          placeholder={
            <div className={cn('flex items-center gap-x-2', !disabled && 'text-white-800')}>
              <SearchIcon />
              <p>Pesquisar produtos</p>
            </div>
          }
          isLoading={isAllowedCategoriesAndProductsPending}
          options={
            allowedProductsOptions?.filter((productOption) => {
              return !entryProducts?.some(({product}) => product?.id === productOption.value)
            }) || []
          }
          onInputChange={setSearchProduct}
          filterOption={
            filterOptions as unknown as (option: FilterOptionOption<unknown>, inputValue: string) => boolean
          }
          value=""
          onChange={(newValue) => {
            appendEntryProduct({
              product: {
                id: (newValue as Option).value,
                display_name: (newValue as ClientCompanyAllowedCategoryAndProductData).display_name,
                unit_for_quantity: (newValue as ClientCompanyAllowedCategoryAndProductData).unit_for_quantity,
                category: {
                  id: '',
                  display_name: '',
                  products_count: '',
                },
              },
              total_quantity: '',
            });

            setProductCategoryTotalQuantity();
          }}
          isDisabled={disabled}
        />

        {entryProducts?.map((entryProduct, index) => (
          <EntryProductFormItemQuantity
            key={entryProduct?.product?.id}
            entryProductIndex={index}
            entryProduct={entryProduct as CompanyGovernmentAllocationEntryProduct}
            onDelete={() => {
              removeEntryProduct(index);
              setProductCategoryTotalQuantity();
            }}
            disabled={disabled}
            allowedProductSummary={allowedCategoriesAndProducts?.products?.[entryProduct?.product?.id]}
            onTotalQuantityChange={setProductCategoryTotalQuantity}
          />
        ))}
      </Box>
    </div>
  )
}
